import React, { useState, useEffect, useCallback } from 'react';
import empresaService          from '../services/empresa.service';
import condicaoPagamentoService from '../services/condicaoPagamento.service';

const COR = {
  azul:     '#003399', azulBg: '#eff6ff',
  borda:    '#e8eaed', texto:  '#1a1a2e', muted: '#888',
  branco:   '#fff',    cinzaBg: '#fafafa',
  vermelho: '#e53935', vermBg:  '#fff0f0',
  verde:    '#1D9E75', verdeBg: '#f0fff4',
  amarelo:  '#d97706', amlBg:   '#fffbeb',
};

const inputStyle = {
  width: '100%', height: '34px', padding: '0 10px',
  border: `1px solid ${COR.borda}`, borderRadius: '5px',
  fontSize: '13px', boxSizing: 'border-box',
  backgroundColor: COR.branco, color: COR.texto, outline: 'none',
};
const inputDisabledStyle = {
  ...inputStyle, backgroundColor: COR.cinzaBg, color: COR.muted,
};
const selectStyle = { ...inputStyle, cursor: 'pointer' };

// ─── Helpers ────────────────────────────────────────────────────────────────
function formatBR(n, casas = 2) {
  if (n === null || n === undefined || n === '') return '';
  const num = parseFloat(n);
  if (isNaN(num)) return '';
  return num.toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas });
}

// Máscara calculadora
function centavosParaBR(input) {
  if (input === '' || input === null || input === undefined) return '';
  const digitos = String(input).replace(/\D/g, '');
  if (!digitos) return '';
  const num = parseInt(digitos, 10) / 100;
  return num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function parseBR(v) {
  if (v === '' || v === null || v === undefined) return 0;
  const norm = String(v).replace(/\./g, '').replace(',', '.');
  const num = parseFloat(norm);
  return isNaN(num) ? 0 : num;
}

// Formata data ISO (YYYY-MM-DD) pra DD/MM/YYYY ou retorna ''
function isoParaBR(iso) {
  if (!iso) return '';
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
}

function hojeISO() {
  return new Date().toISOString().slice(0, 10);
}

// Adiciona N meses a uma data, mantendo o mesmo dia.
// Se o dia não existir no mês de destino, retorna o último dia do mês.
// Ex: 31/01 + 1 mês = 28/02 (ou 29/02 em ano bissexto); 02/05 + 2 meses = 02/07
function addMonthsKeepDay(date, months) {
  const year = date.getFullYear();
  const month = date.getMonth() + months;
  const dia = date.getDate();
  // Cria com dia=1 pra evitar overflow do navegador (que jogaria pro mês seguinte)
  const dt = new Date(year, month, 1, 12, 0, 0);
  // Último dia do mês alvo
  const ultimoDia = new Date(dt.getFullYear(), dt.getMonth() + 1, 0).getDate();
  dt.setDate(Math.min(dia, ultimoDia));
  return dt;
}

// Gera datas de parcelas a partir da condição (string "0,15,30") + data de faturamento.
//
// Regras:
//   - dia=0 → vence na data de faturamento + 1 (D+1)
//   - dia múltiplo de 30 (30, 60, 90...) → mesmo dia, N meses depois
//     (mas se o dia não existir no mês, usa o último dia)
//   - outros valores (15, 7, 45...) → soma N dias literais
function gerarDatasParcelas(condicaoStr, dataFatISO) {
  if (!condicaoStr || !dataFatISO) return [];
  const dias = String(condicaoStr).split(/[,;]/)
    .map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n) && n >= 0);
  const m = dataFatISO.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return [];
  const baseDate = new Date(parseInt(m[1]), parseInt(m[2]) - 1, parseInt(m[3]), 12, 0, 0);

  return dias.map((d) => {
    let dt;
    if (d === 0) {
      // À vista... mas SEMPRE vence em D+1 (regra do usuário)
      dt = new Date(baseDate);
      dt.setDate(dt.getDate() + 1);
    } else if (d % 30 === 0) {
      // Múltiplo de 30 → trata como mês (D+30 = +1 mês, D+60 = +2 meses...)
      dt = addMonthsKeepDay(baseDate, d / 30);
    } else {
      // Dias literais (D+15, D+7, D+45...)
      dt = new Date(baseDate);
      dt.setDate(dt.getDate() + d);
    }
    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    const dd = String(dt.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  });
}

// Gera valores das parcelas (divide igualmente, ajustando última pra fechar)
function gerarValoresParcelas(qtd, valorTotal) {
  if (qtd <= 0 || !valorTotal) return [];
  const valor = Math.floor((valorTotal * 100) / qtd) / 100;
  const totalParcial = valor * (qtd - 1);
  const ultimo = Math.round((valorTotal - totalParcial) * 100) / 100;
  return Array(qtd).fill(0).map((_, i) => i === qtd - 1 ? ultimo : valor);
}

// ────────────────────────────────────────────────────────────────────────────
function FecharNegocioModal({ aberto, negocio, usuarioId, onFechar, onSucesso }) {
  // Combos
  const [empresas, setEmpresas]             = useState([]);
  const [condicoes, setCondicoes]           = useState([]);

  // Form
  const [empresaId, setEmpresaId]           = useState('');
  const [dataFat, setDataFat]               = useState(hojeISO());
  const [pctDesconto, setPctDesconto]       = useState('0,00');
  const [valorDesconto, setValorDesconto]   = useState('0,00');
  const [pctImposto, setPctImposto]         = useState('0,00');
  const [pctCustoOper, setPctCustoOper]     = useState('0,00');
  // Comissão vem do negócio (já foi setada na quantificação ou cliente).
  // % é read-only no modal de fechamento. O VALOR é calculado dinamicamente
  // a partir do valor_atual (não armazenado em state).
  const [pctComissao, setPctComissao]       = useState('0,00');
  const [valorAcrescimo, setValorAcrescimo] = useState('0,00');
  const [condicaoId, setCondicaoId]         = useState('');
  const [parcelas, setParcelas]             = useState([]);

  // Eventos da UI
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando]     = useState(false);
  const [erro, setErro]             = useState('');

  const valorBruto = parseFloat(negocio?.valor_previsto) || 0;

  // ─── Carregar combos ao abrir ─────────────────────────────────────────────
  const carregarCombos = useCallback(async () => {
    try {
      const [emps, conds] = await Promise.all([
        empresaService.listar(),
        condicaoPagamentoService.listar('A'),
      ]);
      setEmpresas(emps.filter(e => e.status === 'A'));
      setCondicoes(conds);
    } catch (err) {
      console.error('Erro ao carregar combos:', err);
    }
  }, []);

  // ─── Reset/preload ao abrir ───────────────────────────────────────────────
  useEffect(() => {
    if (!aberto) return;
    carregarCombos();

    // Se o negócio já tem dados de fechamento (caso reabertura), pré-preenche
    setEmpresaId(negocio?.empresa_faturamento_id || '');
    setDataFat(negocio?.data_faturamento
      ? String(negocio.data_faturamento).slice(0, 10)
      : hojeISO());
    setPctDesconto(formatBR(negocio?.pct_desconto || 0));
    setValorDesconto(formatBR(negocio?.valor_desconto || 0));
    setPctImposto(formatBR(negocio?.pct_imposto || 0));
    setPctCustoOper(formatBR(negocio?.pct_custo_oper || 0));
    // Comissão: pct vem do negócio (definido na quantificação)
    setPctComissao(formatBR(negocio?.pct_comissao || 0));
    setValorAcrescimo(formatBR(negocio?.valor_acrescimo || 0));
    setCondicaoId(negocio?.condicao_pagamento_id || '');
    setParcelas([]);
    setErro('');
  }, [aberto, negocio, carregarCombos]);

  // ─── Quando muda empresa, busca % vigentes ────────────────────────────────
  useEffect(() => {
    if (!empresaId) {
      setPctImposto('0,00');
      setPctCustoOper('0,00');
      return;
    }
    let cancelado = false;
    (async () => {
      try {
        setCarregando(true);
        const r = await empresaService.percentuaisVigentes(empresaId);
        if (cancelado) return;
        const pi = r.pct_imposto != null ? r.pct_imposto : 0;
        const pc = r.pct_custo_oper != null ? r.pct_custo_oper : 0;
        setPctImposto(formatBR(pi));
        setPctCustoOper(formatBR(pc));
      } catch (err) {
        console.error('Erro ao buscar percentuais:', err);
      } finally {
        if (!cancelado) setCarregando(false);
      }
    })();
    return () => { cancelado = true; };
  }, [empresaId]);

  // ─── Sincroniza % desconto ↔ valor desconto ──────────────────────────────
  const handlePctDesconto = (v) => {
    const fmt = centavosParaBR(v);
    setPctDesconto(fmt);
    const pct = parseBR(fmt);
    setValorDesconto(formatBR(valorBruto * pct / 100));
  };
  const handleValorDesconto = (v) => {
    const fmt = centavosParaBR(v);
    setValorDesconto(fmt);
    const val = parseBR(fmt);
    setPctDesconto(valorBruto > 0 ? formatBR((val / valorBruto) * 100) : '0,00');
  };

  // ─── Cálculo do Valor Atual (precisa vir ANTES do useEffect parcelas, ─────
  // que depende de valorAtual pra gerar os valores das parcelas) ────────────
  const desconto    = parseBR(valorDesconto);
  const acrescimo   = parseBR(valorAcrescimo);
  const valorAtual  = valorBruto - desconto + acrescimo;

  // ─── Quando muda condição/data/valor atual → regera parcelas ────────────
  useEffect(() => {
    if (!condicaoId || !dataFat || !valorAtual) { setParcelas([]); return; }
    const cond = condicoes.find(c => c.id === condicaoId);
    if (!cond) { setParcelas([]); return; }
    const datas = gerarDatasParcelas(cond.condicao, dataFat);
    const valores = gerarValoresParcelas(datas.length, valorAtual);
    setParcelas(datas.map((d, i) => ({
      data_vencimento: d,
      valor: formatBR(valores[i]),
    })));
  }, [condicaoId, dataFat, valorAtual, condicoes]);

  // ─── Cálculo da nova fórmula ──────────────────────────────────────────────
  // A = Valor Hora (vem do cliente)
  // B = Valor Bruto (horas × A) — vem do negócio
  // C = % Desconto                           (manual ou calculado de D)
  // D = Valor Desconto (B × C/100)           (manual ou calculado de C)
  // E = Valor Acréscimo                      (manual)
  // F = B − D + E                             ← Valor ATUAL (calculado acima)
  // G = % Comissão                            (vem do vendedor)
  // H = F × G/100                             ← comissão sobre o ATUAL
  // I = % Imposto                             (vem da empresa)
  // J = F × I/100                             ← imposto sobre o ATUAL
  // K = % Custo Op                            (vem da empresa)
  // L = F × K/100                             ← custo op sobre o ATUAL
  // M = F − H − J − L                         ← Líquido
  const pctG  = parseBR(pctComissao);
  const pctI  = parseBR(pctImposto);
  const pctK  = parseBR(pctCustoOper);

  const comissao  = valorAtual * pctG / 100;
  const imposto   = valorAtual * pctI / 100;
  const custoOper = valorAtual * pctK / 100;

  const valorLiquido = valorAtual - comissao - imposto - custoOper;

  // ─── Soma parcelas e diferença ────────────────────────────────────────────
  // A soma deve bater com o VALOR ATUAL (Valor Bruto - Desconto + Acréscimo).
  // É o que o cliente paga, então é o total das parcelas.
  const somaParcelas = parcelas.reduce((s, p) => s + parseBR(p.valor), 0);
  const diferenca    = somaParcelas - valorAtual;
  const somaOk       = Math.abs(diferenca) < 0.01;

  // ─── Atualizar parcela ────────────────────────────────────────────────────
  const atualizarParcela = (idx, campo, valor) => {
    setParcelas(ps => ps.map((p, i) => i === idx ? { ...p, [campo]: valor } : p));
  };

  // ─── Salvar (chama o callback do componente pai) ──────────────────────────
  const salvar = async () => {
    setErro('');
    if (!empresaId)   return setErro('Selecione a empresa de faturamento.');
    if (!dataFat)     return setErro('Informe a data de faturamento.');
    if (!condicaoId)  return setErro('Selecione a condição de pagamento.');
    if (parcelas.length === 0) return setErro('Pelo menos uma parcela.');
    if (!somaOk) {
      return setErro(`A soma das parcelas (R$ ${formatBR(somaParcelas)}) deve ser igual ao Valor Atual (R$ ${formatBR(valorAtual)}).`);
    }
    for (const p of parcelas) {
      if (!p.data_vencimento) return setErro('Toda parcela precisa de data de vencimento.');
      if (parseBR(p.valor) <= 0) return setErro('Toda parcela precisa de valor maior que zero.');
    }

    const dados = {
      empresa_faturamento_id: empresaId,
      data_faturamento:       dataFat,
      pct_desconto:    parseBR(pctDesconto),
      valor_desconto:  desconto,
      pct_imposto:     pctI,
      valor_imposto:   imposto,
      pct_custo_oper:  pctK,
      valor_custo_oper: custoOper,
      pct_comissao:    pctG,
      valor_comissao:  comissao,
      valor_acrescimo: acrescimo,
      valor_atual:     valorAtual,
      valor_liquido:   valorLiquido,
      condicao_pagamento_id: condicaoId,
      parcelas: parcelas.map(p => ({
        data_vencimento: p.data_vencimento,
        valor: parseBR(p.valor),
      })),
    };

    try {
      setSalvando(true);
      if (onSucesso) await onSucesso(dados);
    } catch (err) {
      setErro(err.response?.data?.mensagem || 'Erro ao salvar fechamento.');
    } finally {
      setSalvando(false);
    }
  };

  if (!aberto) return null;

  const Z_OVERLAY = 10000;
  const Z_MODAL = 10001;

  return (
    <>
      <div onClick={onFechar} style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.4)', zIndex: Z_OVERLAY,
      }} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '92%', maxWidth: '900px', maxHeight: '92vh',
        backgroundColor: COR.branco, borderRadius: '8px',
        boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
        display: 'flex', flexDirection: 'column',
        zIndex: Z_MODAL, overflow: 'hidden',
        fontFamily: "'Manrope', sans-serif",
      }}>
        {/* Header */}
        <div style={{
          padding: '14px 20px', borderBottom: `1px solid ${COR.borda}`,
          display: 'flex', alignItems: 'center', gap: '12px',
          backgroundColor: COR.azul, color: COR.branco,
        }}>
          <span style={{ fontSize: '18px' }}>🎯</span>
          <span style={{ fontSize: '15px', fontWeight: '600', flex: 1 }}>
            Fechar Negócio · {negocio?.id} · {negocio?.descricao}
          </span>
          <button onClick={onFechar} style={{
            border: 'none', background: 'transparent', color: COR.branco,
            fontSize: '20px', cursor: 'pointer',
          }}>×</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '14px 18px' }}>
          {erro && (
            <div style={{
              backgroundColor: COR.vermBg, border: '1px solid #ffcccc',
              borderRadius: '5px', color: COR.vermelho, fontSize: '12px',
              padding: '8px 10px', marginBottom: '10px',
            }}>{erro}</div>
          )}

          {/* Valor Bruto destaque */}
          <div style={{
            backgroundColor: COR.azulBg, border: `1px solid ${COR.azul}33`,
            borderRadius: '6px', padding: '10px 14px', marginBottom: '14px',
            display: 'flex', alignItems: 'center', gap: '10px',
          }}>
            <span style={{ fontSize: '11px', fontWeight: '700', color: COR.azul,
                           textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Valor Bruto
            </span>
            <span style={{ fontSize: '20px', fontWeight: '700', color: COR.azul, marginLeft: 'auto' }}>
              R$ {formatBR(valorBruto)}
            </span>
          </div>

          {/* SEÇÃO 1: Faturamento */}
          <Bloco titulo="Faturamento">
            <Linha>
              <Campo label="Empresa de Faturamento" obrigatorio largura="50%">
                <select value={empresaId} onChange={e => setEmpresaId(e.target.value)}
                  style={selectStyle}>
                  <option value="">Selecione...</option>
                  {empresas.map(e => (
                    <option key={e.id} value={e.id}>{e.nome}</option>
                  ))}
                </select>
              </Campo>
              <Campo label="Data de Faturamento" obrigatorio largura="25%">
                <input type="date" value={dataFat}
                  onChange={e => setDataFat(e.target.value)}
                  style={inputStyle} />
              </Campo>
              <Campo label="Condição de Pagamento" obrigatorio largura="25%">
                <select value={condicaoId} onChange={e => setCondicaoId(e.target.value)}
                  style={selectStyle}>
                  <option value="">Selecione...</option>
                  {condicoes.map(c => (
                    <option key={c.id} value={c.id}>{c.descricao}</option>
                  ))}
                </select>
              </Campo>
            </Linha>
          </Bloco>

          {/* SEÇÃO 2: Reduções */}
          <Bloco titulo="Cálculo de Valores">
            {/* Linha 1: Desconto + Acréscimo */}
            <Linha>
              <Campo label="% Desconto" largura="25%">
                <input value={pctDesconto}
                  onChange={e => handlePctDesconto(e.target.value)}
                  style={{ ...inputStyle, textAlign: 'right' }} />
              </Campo>
              <Campo label="Valor Desconto (R$)" largura="25%">
                <input value={valorDesconto}
                  onChange={e => handleValorDesconto(e.target.value)}
                  style={{ ...inputStyle, textAlign: 'right' }} />
              </Campo>
              <Campo label="Valor Acréscimo (R$)" largura="25%">
                <input value={valorAcrescimo}
                  onChange={e => setValorAcrescimo(centavosParaBR(e.target.value))}
                  style={{ ...inputStyle, textAlign: 'right' }} />
              </Campo>
              <Campo label="Valor Atual (R$)" largura="25%">
                <input value={formatBR(valorAtual)} disabled
                  style={{ ...inputDisabledStyle, textAlign: 'right',
                    fontWeight: '700', color: COR.azul }}
                  title="Valor Bruto − Desconto + Acréscimo" />
              </Campo>
            </Linha>

            {/* Linha 2: Comissão */}
            <Linha>
              <Campo label="% Comissão" largura="50%">
                <input value={pctComissao} disabled
                  style={{ ...inputDisabledStyle, textAlign: 'right' }}
                  title="Vem do cadastro do vendedor" />
              </Campo>
              <Campo label="Valor Comissão (R$)" largura="50%">
                <input value={formatBR(comissao)} disabled
                  style={{ ...inputDisabledStyle, textAlign: 'right' }}
                  title="% Comissão × Valor Atual" />
              </Campo>
            </Linha>

            {/* Linha 3: Imposto */}
            <Linha>
              <Campo label="% Imposto" largura="50%">
                <input value={pctImposto} disabled
                  style={{ ...inputDisabledStyle, textAlign: 'right' }}
                  title="Vem do cadastro da empresa de faturamento" />
              </Campo>
              <Campo label="Valor Imposto (R$)" largura="50%">
                <input value={formatBR(imposto)} disabled
                  style={{ ...inputDisabledStyle, textAlign: 'right' }}
                  title="% Imposto × Valor Atual" />
              </Campo>
            </Linha>

            {/* Linha 4: Custo Operacional */}
            <Linha>
              <Campo label="% Custo Operacional" largura="50%">
                <input value={pctCustoOper} disabled
                  style={{ ...inputDisabledStyle, textAlign: 'right' }}
                  title="Vem do cadastro da empresa de faturamento" />
              </Campo>
              <Campo label="Valor Custo Op. (R$)" largura="50%">
                <input value={formatBR(custoOper)} disabled
                  style={{ ...inputDisabledStyle, textAlign: 'right' }}
                  title="% Custo Op × Valor Atual" />
              </Campo>
            </Linha>
          </Bloco>

          {/* Valor Líquido destaque */}
          <div style={{
            backgroundColor: COR.verdeBg, border: `1px solid ${COR.verde}55`,
            borderRadius: '6px', padding: '12px 14px', marginBottom: '14px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
              <span style={{ fontSize: '11px', fontWeight: '700', color: COR.verde,
                             textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Valor Líquido
              </span>
              <span style={{ fontSize: '22px', fontWeight: '700', color: COR.verde, marginLeft: 'auto' }}>
                R$ {formatBR(valorLiquido)}
              </span>
            </div>
            <div style={{ fontSize: '11px', color: COR.muted, fontVariantNumeric: 'tabular-nums' }}>
              {formatBR(valorAtual)} − ({formatBR(comissao)} + {formatBR(imposto)} + {formatBR(custoOper)}) = {formatBR(valorLiquido)}
            </div>
          </div>

          {/* SEÇÃO 3: Parcelas */}
          <Bloco titulo={`Parcelas (${parcelas.length})`}>
            {parcelas.length === 0 ? (
              <div style={{
                padding: '20px', textAlign: 'center', color: COR.muted, fontSize: '12px',
                backgroundColor: COR.cinzaBg, borderRadius: '5px',
              }}>
                Selecione uma condição de pagamento e data de faturamento pra gerar as parcelas.
              </div>
            ) : (
              <>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                  <thead>
                    <tr style={{ backgroundColor: COR.cinzaBg }}>
                      <th style={ThStyle}>#</th>
                      <th style={ThStyle}>Data Vencimento</th>
                      <th style={ThStyle}>Valor (R$)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parcelas.map((p, idx) => (
                      <tr key={idx} style={{ borderBottom: `1px solid ${COR.borda}` }}>
                        <td style={{ ...TdStyle, fontWeight: '600' }}>{idx + 1}</td>
                        <td style={TdStyle}>
                          <input type="date" value={p.data_vencimento}
                            onChange={e => atualizarParcela(idx, 'data_vencimento', e.target.value)}
                            style={inputStyle} />
                        </td>
                        <td style={TdStyle}>
                          <input value={p.valor}
                            onChange={e => atualizarParcela(idx, 'valor', centavosParaBR(e.target.value))}
                            style={{ ...inputStyle, textAlign: 'right' }} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Indicador de soma */}
                <div style={{
                  marginTop: '10px', padding: '8px 12px',
                  backgroundColor: somaOk ? COR.verdeBg : COR.vermBg,
                  border: `1px solid ${somaOk ? COR.verde : COR.vermelho}55`,
                  borderRadius: '5px',
                  display: 'flex', alignItems: 'center', gap: '10px',
                  fontSize: '12px',
                }}>
                  <span style={{ fontWeight: '600', color: somaOk ? COR.verde : COR.vermelho }}>
                    {somaOk ? '✓' : '⚠️'} Soma: R$ {formatBR(somaParcelas)}
                  </span>
                  {!somaOk && (
                    <span style={{ color: COR.vermelho }}>
                      (diferença de R$ {formatBR(Math.abs(diferenca))} {diferenca > 0 ? 'a mais' : 'a menos'})
                    </span>
                  )}
                  <span style={{ marginLeft: 'auto', color: COR.muted }}>
                    Esperado: R$ {formatBR(valorAtual)}
                  </span>
                </div>
              </>
            )}
          </Bloco>
        </div>

        {/* Footer com botões */}
        <div style={{
          padding: '12px 20px', borderTop: `1px solid ${COR.borda}`,
          display: 'flex', gap: '10px', justifyContent: 'flex-end',
          backgroundColor: COR.cinzaBg,
        }}>
          <button onClick={onFechar} disabled={salvando}
            style={{
              padding: '8px 18px', fontSize: '13px', fontWeight: '600',
              border: `1px solid ${COR.borda}`, borderRadius: '5px',
              backgroundColor: COR.branco, color: COR.texto,
              cursor: 'pointer',
            }}>Cancelar</button>
          <button onClick={salvar} disabled={salvando || !somaOk}
            style={{
              padding: '8px 22px', fontSize: '13px', fontWeight: '600',
              border: 'none', borderRadius: '5px',
              backgroundColor: somaOk ? COR.verde : COR.muted,
              color: COR.branco,
              cursor: somaOk && !salvando ? 'pointer' : 'not-allowed',
              opacity: salvando ? 0.6 : 1,
            }}>{salvando ? 'Salvando...' : '🎯 Confirmar Fechamento'}</button>
        </div>
      </div>
    </>
  );
}

// Sub-componentes
function Bloco({ titulo, children }) {
  return (
    <div style={{
      border: `1px solid ${COR.borda}`, borderRadius: '6px',
      padding: '12px 14px', marginBottom: '14px',
    }}>
      <div style={{
        fontSize: '11px', fontWeight: '700', color: COR.azul,
        textTransform: 'uppercase', letterSpacing: '0.5px',
        marginBottom: '10px',
      }}>{titulo}</div>
      {children}
    </div>
  );
}

function Linha({ children }) {
  return <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '8px' }}>{children}</div>;
}

function Campo({ label, children, obrigatorio, largura = '100%' }) {
  return (
    <div style={{ width: `calc(${largura} - 8px)`, minWidth: '120px' }}>
      <label style={{
        display: 'block', fontSize: '11px', fontWeight: '600',
        color: COR.muted, marginBottom: '4px',
      }}>
        {label}{obrigatorio && <span style={{ color: COR.vermelho }}> *</span>}
      </label>
      {children}
    </div>
  );
}

const ThStyle = {
  padding: '8px 6px', textAlign: 'left',
  fontSize: '10px', fontWeight: '700', color: COR.muted,
  textTransform: 'uppercase', letterSpacing: '0.4px',
};
const TdStyle = { padding: '6px 4px', verticalAlign: 'middle' };

export default FecharNegocioModal;
